import React, { useEffect, useRef } from 'react';
import cn from 'classnames';

import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { PROJECT_TABLES_STATE, ProjectTableSortKey, setTableSort } from '../../_mediator/projects.slice';
import {
  PROJECTS_STORIES_HEADERS,
  PROJECTS_FILES_HEADERS,
  PROJECTS_LIST_HEADERS,
  PROJECTS_FINALCUTS_HEADERS,
} from '../../_mediator/projects.params';

import { ReactComponent as ArrowIcon } from 'assets/nav-pane/arrow.svg';
import styles from '../_sections.module.scss';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  headers:
    | readonly (typeof PROJECTS_STORIES_HEADERS)[Index][]
    | readonly (typeof PROJECTS_FILES_HEADERS)[Index][]
    | readonly (typeof PROJECTS_LIST_HEADERS)[Index][]
    | readonly (typeof PROJECTS_FINALCUTS_HEADERS)[Index][];
  sortKey: ProjectTableSortKey;
  selectedRow?: number;
}

export const ProjectsTable = ({ headers, sortKey, children, selectedRow }: Props) => {
  const project = useAppSelector((state) => state.projects);
  const dispatch = useAppDispatch();

  const timeoutId = useRef<NodeJS.Timeout>();
  const tableRef = useRef<HTMLTableElement>(null);
  const sectionEl = useRef<HTMLElement | null>(null);
  const selectedEl = useRef<HTMLElement | null>(null);

  const handleSortByColumn = (cell: number) => {
    dispatch(
      setTableSort({
        key: sortKey,
        value:
          project[sortKey].column === cell
            ? { ...project[sortKey], order: project[sortKey].order === 'ASC' ? 'DESC' : 'ASC' }
            : { column: cell, order: 'ASC' },
      })
    );
  };

  const scrollElement = () => {
    if (!sectionEl.current || !selectedEl.current) return;
    if (isElementInView(sectionEl.current, selectedEl.current)) return;
    selectedEl.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  useEffect(() => {
    sectionEl.current = tableRef.current!.closest('div.' + styles.SectionBody) as HTMLDivElement;
  }, []);

  useEffect(() => {
    const elem = tableRef.current!.querySelector<HTMLElement>('tr.' + styles.selected);
    selectedEl.current = elem;
  }, [selectedRow]);

  useEffect(() => {
    clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(scrollElement, 500);
  }, [...PROJECT_TABLES_STATE.map((state) => project[state])]);

  useEffect(() => {
    scrollElement();
  }, [selectedRow, project[sortKey]]);

  return (
    <div className={styles.TableContainer}>
      <table className={styles.Table} ref={tableRef}>
        <thead>
          <tr>
            {headers.map((title, index) => (
              <td
                key={index}
                onClick={() => handleSortByColumn(index)}
                className={cn({ [styles.selected]: project[sortKey].column === index })}
              >
                {title}
                <ArrowIcon
                  className={cn(styles.sortIcon, {
                    [styles.desc]: project[sortKey].order === 'DESC' && project[sortKey].column === index,
                  })}
                />
              </td>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
};

/* H E L P E R S */

const isElementInView = (container: HTMLElement, element: HTMLElement, tolerance = 1, offsetTop = 33) => {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  return (
    elementRect.top >= containerRect.top + tolerance + offsetTop &&
    elementRect.bottom <= containerRect.bottom - tolerance
  );
};
